import type { Service } from '@/types/booking';

interface BuildDisplayServicesParams {
  services: Service[] | undefined;
  isAnyProfessional: boolean;
  /** Resolved barber id for the current appointment (specific-barber path). */
  barberId: string | undefined;
  /**
   * Full record (with `.assignments`) of the already-selected primary
   * service, when one exists. Only relevant on the "Any Professional" path —
   * used to filter which services can be offered alongside it (see
   * `isCompatibleAddonCandidate`).
   */
  primaryService?: Service;
  /**
   * Full records of every service already in the cart. When non-empty on the
   * "Any Professional" path it replaces `primaryService`: a service is listed
   * only if one professional can perform it with the whole cart (see
   * `isCompatibleWithCart`).
   */
  cartServices?: Service[];
}

function canBePerformedAlongside(candidate: Service, other: Service): boolean {
  if (!other.assignments?.length) return true;

  const barbersWhoOfferOther = new Set(
    other.assignments.map((assignment) => assignment.assigneeId),
  );

  return candidate.assignments.some(
    (assignment) =>
      assignment.assignmentType === 'shop' ||
      barbersWhoOfferOther.has(assignment.assigneeId),
  );
}

/**
 * In "Any Professional" bookings no barber is chosen yet, so a candidate
 * offered alongside the selected primary must be performable by at least one
 * barber who can also perform the primary — otherwise time-slot search later
 * comes back empty because no single barber offers both. Flagship (like
 * water-on-web) doesn't have true add-ons yet: `ServiceGrid`'s "Anything you
 * wish to add?" section is every other service in the shop, not just the
 * `isAddonService` ones, so this check applies to all of them. Mirrors
 * water-on-web's `patchServiceAddonsWithRestOfServices`
 * (packages/repository/src/repositories/service.ts).
 */
function isCompatibleAddonCandidate(
  candidate: Service,
  primary: Service,
): boolean {
  if (candidate.id === primary.id) return false;
  return canBePerformedAlongside(candidate, primary);
}

/**
 * True when one professional can perform `candidate` together with every
 * cart service: the eligible professionals are the intersection of the cart
 * services' assignees, and a cart service without assignments restricts
 * nothing. A cart service is always compatible, so it never filters itself out.
 */
export function isCompatibleWithCart(
  candidate: Service,
  cartServices: Service[],
): boolean {
  if (cartServices.some((cartService) => cartService.id === candidate.id)) {
    return true;
  }

  const restrictingAssigneeIds = cartServices
    .filter((cartService) => cartService.assignments?.length)
    .map(
      (cartService) =>
        new Set(
          cartService.assignments.map((assignment) => assignment.assigneeId),
        ),
    );
  if (!restrictingAssigneeIds.length) return true;

  const [first, ...rest] = restrictingAssigneeIds;
  const eligibleAssigneeIds = [...first].filter((id) =>
    rest.every((assigneeIds) => assigneeIds.has(id)),
  );

  return candidate.assignments.some(
    (assignment) =>
      assignment.assignmentType === 'shop' ||
      eligibleAssigneeIds.includes(assignment.assigneeId),
  );
}

/**
 * Derives the list of services to display from the fetched services,
 * applying professional-specific filtering and price/duration resolution.
 * Pure function (no hooks) so it can be unit-tested and shared between
 * booking's ChooseServiceScreen and the standby funnel's service screens.
 *
 * - "Any Professional": show every service with at least one barber assignment,
 *   keeping cost/duration ranges from the shop-level response. When a primary
 *   service is already selected, other services incompatible with it (see
 *   `isCompatibleAddonCandidate`) are excluded — the primary itself is always
 *   kept. When `cartServices` is non-empty it takes over: services must be
 *   performable by one professional together with every cart service.
 * - Specific professional: `services` already comes from the barber-scoped
 *   endpoint (server-side scoped), so we do NOT re-filter by assignment
 *   (SQR-14002 hardening — a shop/brand assignment must not be dropped). The
 *   barber-scoped service already carries the barber's exact cost/duration at
 *   the top level; the matching assignment (when present, e.g. merged from
 *   shop-level) supplies `parentId` and any per-barber overrides.
 */
export function buildDisplayServices({
  services,
  isAnyProfessional,
  barberId,
  primaryService,
  cartServices,
}: BuildDisplayServicesParams): Service[] {
  const barberServices = isAnyProfessional
    ? (services || []).filter((service) =>
        service.assignments.some(
          (assignment) => assignment.assignmentType === 'barber',
        ),
      )
    : services || [];

  const hasCart = isAnyProfessional && !!cartServices?.length;
  let compatibleServices = barberServices;
  if (hasCart) {
    compatibleServices = barberServices.filter((service) =>
      isCompatibleWithCart(service, cartServices),
    );
  } else if (isAnyProfessional && primaryService) {
    compatibleServices = barberServices.filter(
      (service) =>
        service.id === primaryService.id ||
        isCompatibleAddonCandidate(service, primaryService),
    );
  }

  return compatibleServices.map((service) => {
    if (!isAnyProfessional) {
      const assignedService = service.assignments.find(
        (assignment) =>
          assignment.assignmentType === 'barber' &&
          assignment.assigneeId === barberId,
      );

      return {
        ...service,
        parentId: assignedService?.parentId ?? service.parentId,
        cost: assignedService?.cost ?? service.cost,
        duration: assignedService?.duration ?? service.duration,
        costRange: undefined,
        durationRange: undefined,
      };
    }

    const assignedServiceAny = service.assignments.find(
      (assignment) =>
        assignment.serviceId === service.id ||
        assignment.parentId === service.id,
    );

    return assignedServiceAny
      ? { ...service, parentId: assignedServiceAny.parentId }
      : service;
  });
}
